import { createApi } from '@reduxjs/toolkit/query/react';

import { apiSahServiceTypeAssociations } from '~constants/api';

import { Request, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import { SahServiceTypeAssociation } from '~lookups/models';

import { ServiceTypeRow } from '~appointments/reducers/serviceTypes';

export const typePrefix = `sah-service-type-associations`;

type AssocsResponse = {
  rows: SahServiceTypeAssociation[];
  total: number;
};

type SahServiceResponse = {
  id: string;
  items: any[];
  wraps: any[];
};

export const sahServiceTypeAssociationsApi = createApi({
  reducerPath: 'sahServiceTypeAssociationsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahServiceTypeAssociations }),
  tagTypes: ['sah-service-type-associations'],
  endpoints: (builder) => ({
    getAssocs: builder.query<AssocsResponse, Request | void>({
      query: (params) => ({ url: '', method: 'GET', params: { sort: ['category.name', 'name'], ...params } }),
      providesTags: [{ type: 'sah-service-type-associations', id: 'LIST' }],
      transformResponse: (payload) => unwrap<AssocsResponse>(payload),
    }),
    updateAssoc: builder.mutation<void, Request>({
      query: (body) => ({ url: '', method: 'POST', body }),
      invalidatesTags: [{ type: 'sah-service-type-associations', id: 'LIST' }],
      transformResponse: (payload) => unwrap<void>(payload),
    }),
    // `date` is the date the work was delivered. A service type can be associated with more than
    // one Support at Home service, one per period, and each period carries its own wraps and items
    // - so a line has to be offered the period covering its own service date, not whichever one is
    // in effect while the provider is typing. Omitted, the API falls back to today as before.
    getAssocByServiceType: builder.query<SahServiceResponse, { id: string; date?: string }>({
      query: ({ id, date }) => ({ url: `/service-type/${id}`, method: 'GET', params: date ? { date } : undefined }),
      providesTags: (_result, _error, { id }) => [{ type: 'sah-service-type-associations', id }],
      transformResponse: (payload) => unwrap<SahServiceResponse>(payload),
    }),
    getAssignableServiceTypes: builder.query<ServiceTypeRow[], Request | void>({
      query: () => ({
        url: `/assignable-service-types`,
        method: 'GET',
      }),
      providesTags: [{ type: 'sah-service-type-associations', id: 'LIST' }],
      transformResponse: (payload) => unwrap<ServiceTypeRow[]>(payload),
    }),
  }),
});

export const {
  useGetAssocsQuery,
  useUpdateAssocMutation,
  useLazyGetAssocByServiceTypeQuery,
  useGetAssignableServiceTypesQuery,
} = sahServiceTypeAssociationsApi;
